import { formatDistance } from './format';

export type PlayerRow = { id: string; name: string; ship: string; color: string; distance: number; score: number; self: boolean; where: string };

const COLOR_HEX: Record<string, string> = { blue: '#3f7bff', green: '#3fcf6f', orange: '#ff9a3c', purple: '#a86bff', red: '#ff4b4b' };

/** Chat feed + input (Enter) and the player list (Tab). */
export class Social {
  private readonly feed: HTMLDivElement;
  private readonly input: HTMLInputElement;
  private readonly list: HTMLDivElement;
  private readonly net: HTMLDivElement;
  chatOpen = false;
  listOpen = false;
  onSend: (text: string) => void = () => {};
  onSelectPlayer: (id: string) => void = () => {};

  constructor(parent: HTMLElement) {
    const root = document.createElement('div');
    root.className = 'social';
    root.innerHTML = `
      <div class="chat-feed"></div>
      <input class="chat-input hidden" maxlength="200" placeholder="Say something · Enter to send · Esc to cancel" />
      <div class="player-list hidden"></div>
      <div class="net-status"></div>`;
    parent.appendChild(root);
    this.feed = root.querySelector('.chat-feed')!;
    this.input = root.querySelector('.chat-input')!;
    this.list = root.querySelector('.player-list')!;
    this.net = root.querySelector('.net-status')!;
    this.input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        const text = this.input.value.trim();
        if (text) this.onSend(text);
        this.closeChat();
      }
      if (e.key === 'Escape') this.closeChat();
    });
    this.list.addEventListener('click', (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-id]');
      if (row?.dataset.id) this.onSelectPlayer(row.dataset.id);
    });
  }

  setVisible(v: boolean): void {
    (this.feed.parentElement as HTMLElement).style.display = v ? '' : 'none';
  }

  setStatus(s: 'connecting' | 'online' | 'offline', players: number): void {
    this.net.textContent = s === 'online' ? `● ONLINE · ${players} pilot${players === 1 ? '' : 's'}` : s === 'connecting' ? '○ CONNECTING…' : '○ OFFLINE · retrying';
    this.net.className = `net-status ${s}`;
  }

  openChat(): void {
    this.chatOpen = true;
    this.input.classList.remove('hidden');
    document.exitPointerLock?.();
    setTimeout(() => this.input.focus(), 0);
  }

  closeChat(): void {
    this.chatOpen = false;
    this.input.value = '';
    this.input.classList.add('hidden');
    this.input.blur();
  }

  message(name: string, text: string, color = '#7fe7ff', system = false): void {
    const line = document.createElement('div');
    line.className = system ? 'chat-line system' : 'chat-line';
    if (!system) {
      const n = document.createElement('b');
      n.textContent = name;
      n.style.color = color;
      line.appendChild(n);
    }
    const t = document.createElement('span');
    t.textContent = text;
    line.appendChild(t);
    this.feed.appendChild(line);
    while (this.feed.children.length > 8) this.feed.firstChild!.remove();
    setTimeout(() => line.classList.add('old'), 12000);
  }

  toggleList(): void {
    this.listOpen = !this.listOpen;
    this.list.classList.toggle('hidden', !this.listOpen);
  }

  renderList(rows: PlayerRow[]): void {
    if (!this.listOpen) return;
    const html = rows
      .sort((a, b) => b.score - a.score)
      .map(
        (r, i) => `<div class="pl-row${r.self ? ' self' : ''}" data-id="${r.self ? '' : r.id}">
          <span class="pl-rank">${i + 1}</span>
          <span class="pl-dot" style="background:${COLOR_HEX[r.color] ?? '#fff'}"></span>
          <span class="pl-name">${escapeHtml(r.name)}${r.self ? ' (you)' : ''}</span>
          <span class="pl-ship">${escapeHtml(r.ship)}</span>
          <span class="pl-where">${escapeHtml(r.where)}</span>
          <span class="pl-dist">${r.self ? '—' : formatDistance(r.distance)}</span>
          <span class="pl-score">${r.score.toLocaleString('en-US')}</span>
        </div>`,
      )
      .join('');
    this.list.innerHTML = `<div class="pl-head">PILOTS ONLINE · click to target · G to follow</div>${html}`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
